/* eslint-disable max-len */
import { UserStatus } from '@rentguru/commons-utils';
import { FieldFilterResetProvider, LoadingScreen } from '@up2rent/ui';
import { isEmpty, isNil } from 'lodash';
import React, { useEffect, useState } from 'react';

import AccessRevoked from 'src/components/Auth/AccessRevoked';
import { RentTrackingProvider } from 'src/components/Dashboard/rent/RentTrackingContext';
import { InvoiceContextProvider } from 'src/hooks/InvoicesContext';

import { TenantContextProvider } from '../tenantHooks/TenantContext';

import { AccountLabelsContextProvider } from './AccountLabelsContext';
import { AddressContextProvider } from './AddressContext';
import { AgencyRatesContextProvider } from './AgencyRatesContext';
import { BankAccountContextProvider } from './BankAccountsContext';
import { BillingItemsContextProvider } from './BillingItemsContext';
import { BuildingContextProvider } from './BuildingsContext';
import { BuildingSiteContextProvider } from './BuildingSitesContext';
import { ChargeContextProvider } from './ChargesContext';
import { ClientContextProvider } from './ClientContext';
import { endClientSwitch, useIsClientSwitching } from './clientTransition';
import { CommunicationContextProvider } from './CommunicationsContext';
import { CommunicationSettingsProfileContextProvider } from './CommunicationSettingsProfilesContext';
import { ConfirmationDialogProvider } from './ConfirmationDialogContext';
import { ContactContextProvider } from './ContactsContext';
import { ContextLoaderGate, ContextLoaderProvider } from './ContextLoader';
import { CounterpartyIdentitiesContextProvider } from './CounterpartyIdentitiesContext';
import { CustomFieldsContextProvider } from './CustomFieldsContext';
import { CustomSubscriptionContextProvider } from './CustomSubscriptionContext';
import { EntityRelationsContextProvider } from './EntityRelationsContext';
import { ExactOnlineContextProvider } from './ExactOnlineContext';
import { DocumentCategoryContextProvider } from './FileCategoryContext';
import { FilesContextProvider } from './FilesContext';
import { FilterGroupsProvider } from './FilterGroupsContext';
import { FolderDatabaseContextProvider } from './Folderhook/FolderDatabaseContext';
import { FolderWithDBContextProvider } from './Folderhook/FolderWithDBContext';
import { IntegrationContextProvider } from './IntegrationsContext';
import { InvoiceSettingContextProvider } from './InvoiceSettingsContext';
import { LeaseActionHistoryContextProvider } from './LeaseActionHistoriesContext';
import { LeaseContactContextProvider } from './LeaseContactContext';
import { LeasePriceHistoryContextProvider } from './LeasePriceHistoriesContext';
import { LeaseContextProvider } from './LeasesContext';
import { LeaseVariousOperationContextProvider } from './LeaseVariousOperationsContext';
import { MyCompanyProvider } from './MyCompanyContext';
import { NotificationsContextProvider } from './NotificationsContext';
import { PaymentsInvalidationProvider } from './PaymentsInvalidationContext';
import { PostingsContextProvider } from './PostingsContext';
import { ReconciliationContextProvider } from './ReconciliationContext';
import { ReleaseNotesContextProvider } from './ReleaseNotesContext';
import { RuleContextProvider } from './RuleContext';
import { SendingSourceContextProvider } from './SendingSourceContext';
import SessionRevocationListener from './SessionRevocationListener';
import { SettingContextProvider } from './SettingsContext';
import { SignatureDocumentsContextProvider } from './SignatureDocumentContext';
import { TagContextProvider } from './TagContext';
import { TeamsContextProvider } from './TeamsContext';
import { TechnicsContextProvider } from './TechnicsContext';
import { TechnicsLibraryContextProvider } from './TechnicsLibraryContext';
import { TemplateContextProvider } from './TemplatesContext';
import { TicketContextProvider } from './TicketsContext';
import { TransactionContextProvider } from './TransactionsContext';
import { UnitInventoryContextProvider } from './UnitInventoryContext';
import { UnitLeaseContextProvider } from './UnitLeaseContext';
import { UnitContextProvider } from './UnitsContext';
import { useUser } from './UserContext';
import { UserPreferencesContextProvider } from './UserPreferencesContext';
import { UserRolesContextProvider } from './UserRolesContext';
import { UsersContextProvider } from './UsersContext';
import { PermissionsContextProvider, TenantPermissionsContextProvider } from './utils/PermissionsContext';
import { useUp2RentVersion } from './utils/useUp2RentVersion';
import { VariousOperationsContextProvider } from './VariousOperationsContext';
import { VatRatesContextProvider } from './VatRatesContext';

const EntityConditionalHooks: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const { clientId, tenantId, isFetchingUser, userAttributes, memberStatus } = useUser();
  const isAuthenticated = !isEmpty(userAttributes?.email);
  const { shouldClearCache, clearCache } = useUp2RentVersion();
  const isMemberDisabled = isAuthenticated && memberStatus === UserStatus.DISABLED;

  useEffect(() => {
    if (shouldClearCache && isAuthenticated && !isNil(clientId) && !isFetchingUser) {
      clearCache();
    }
  }, [shouldClearCache, clearCache, clientId, isAuthenticated, isFetchingUser]);

  const userTriggeredSwitch = useIsClientSwitching();
  const [revealedClientId, setRevealedClientId] = useState<string | null | undefined>(clientId);
  const clientIdChanged = Boolean(revealedClientId) && revealedClientId !== clientId;
  const isSwitchingClient = userTriggeredSwitch || clientIdChanged;
  useEffect(() => {
    if (clientIdChanged) {
      const id = setTimeout(() => {
        setRevealedClientId(clientId);
        endClientSwitch();
      }, 900);
      return () => clearTimeout(id);
    }
    if (revealedClientId !== clientId) setRevealedClientId(clientId);
  }, [clientId, clientIdChanged, revealedClientId]);

  if (isMemberDisabled) {
    return <AccessRevoked />;
  }

  if (!isAuthenticated || isNil(clientId)) {
    return <>{children}</>;
  }

  if (shouldClearCache) {
    return null;
  }

  if (isSwitchingClient) {
    return <LoadingScreen />;
  }

  if (!isNil(tenantId)) {
    return (
      <TenantPermissionsContextProvider>
        <SessionRevocationListener />
        <FolderDatabaseContextProvider>
          <FieldFilterResetProvider>
            <FilesContextProvider>
              <DocumentCategoryContextProvider>
                <SendingSourceContextProvider>
                  <SignatureDocumentsContextProvider>
                    <CommunicationContextProvider>
                      <TenantContextProvider>{children}</TenantContextProvider>
                    </CommunicationContextProvider>
                  </SignatureDocumentsContextProvider>
                </SendingSourceContextProvider>
              </DocumentCategoryContextProvider>
            </FilesContextProvider>
          </FieldFilterResetProvider>
        </FolderDatabaseContextProvider>
      </TenantPermissionsContextProvider>
    );
  }

  return (
    <React.Fragment key={clientId ?? 'no-client'}>
      <SessionRevocationListener />
      <ContextLoaderProvider>
        <ConfirmationDialogProvider>
          <ClientContextProvider>
            <PermissionsContextProvider>
              <FolderDatabaseContextProvider>
                <FilesContextProvider>
                  <AddressContextProvider>
                    <BuildingContextProvider>
                      <BuildingSiteContextProvider>
                        <UnitContextProvider>
                          <TemplateContextProvider>
                            <UserPreferencesContextProvider>
                              <SettingContextProvider>
                                <CommunicationSettingsProfileContextProvider>
                                  <EntityRelationsContextProvider>
                                    <ContactContextProvider>
                                      <UsersContextProvider>
                                        <UserRolesContextProvider>
                                          <TeamsContextProvider>
                                            <BankAccountContextProvider>
                                              <TicketContextProvider>
                                                <NotificationsContextProvider>
                                                  <RuleContextProvider>
                                                    <TechnicsLibraryContextProvider>
                                                      <TechnicsContextProvider>
                                                        <CustomFieldsContextProvider>
                                                          <TagContextProvider>
                                                            <UnitInventoryContextProvider>
                                                              <SignatureDocumentsContextProvider>
                                                                <LeaseContactContextProvider>
                                                                  <UnitLeaseContextProvider>
                                                                    <LeaseVariousOperationContextProvider>
                                                                      <LeaseContextProvider>
                                                                        <AccountLabelsContextProvider>
                                                                          <CounterpartyIdentitiesContextProvider>
                                                                            <PostingsContextProvider>
                                                                              <TransactionContextProvider>
                                                                                <ReconciliationContextProvider>
                                                                                  <InvoiceContextProvider>
                                                                                    <CommunicationContextProvider>
                                                                                      <VariousOperationsContextProvider>
                                                                                        <CustomSubscriptionContextProvider>
                                                                                          <ReleaseNotesContextProvider>
                                                                                            <AgencyRatesContextProvider>
                                                                                              <BillingItemsContextProvider>
                                                                                                <LeasePriceHistoryContextProvider>
                                                                                                  <LeaseActionHistoryContextProvider>
                                                                                                    <SendingSourceContextProvider>
                                                                                                      <IntegrationContextProvider>
                                                                                                        <ChargeContextProvider>
                                                                                                          <DocumentCategoryContextProvider>
                                                                                                            <FilterGroupsProvider>
                                                                                                              <FolderWithDBContextProvider>
                                                                                                                <VatRatesContextProvider>
                                                                                                                  <MyCompanyProvider>
                                                                                                                    <ExactOnlineContextProvider>
                                                                                                                      <InvoiceSettingContextProvider>
                                                                                                                        <RentTrackingProvider>
                                                                                                                          <PaymentsInvalidationProvider>
                                                                                                                            <ContextLoaderGate>
                                                                                                                              {
                                                                                                                                children
                                                                                                                              }
                                                                                                                            </ContextLoaderGate>
                                                                                                                          </PaymentsInvalidationProvider>
                                                                                                                        </RentTrackingProvider>
                                                                                                                      </InvoiceSettingContextProvider>
                                                                                                                    </ExactOnlineContextProvider>
                                                                                                                  </MyCompanyProvider>
                                                                                                                </VatRatesContextProvider>
                                                                                                              </FolderWithDBContextProvider>
                                                                                                            </FilterGroupsProvider>
                                                                                                          </DocumentCategoryContextProvider>
                                                                                                        </ChargeContextProvider>
                                                                                                      </IntegrationContextProvider>
                                                                                                    </SendingSourceContextProvider>
                                                                                                  </LeaseActionHistoryContextProvider>
                                                                                                </LeasePriceHistoryContextProvider>
                                                                                              </BillingItemsContextProvider>
                                                                                            </AgencyRatesContextProvider>
                                                                                          </ReleaseNotesContextProvider>
                                                                                        </CustomSubscriptionContextProvider>
                                                                                      </VariousOperationsContextProvider>
                                                                                    </CommunicationContextProvider>
                                                                                  </InvoiceContextProvider>
                                                                                </ReconciliationContextProvider>
                                                                              </TransactionContextProvider>
                                                                            </PostingsContextProvider>
                                                                          </CounterpartyIdentitiesContextProvider>
                                                                        </AccountLabelsContextProvider>
                                                                      </LeaseContextProvider>
                                                                    </LeaseVariousOperationContextProvider>
                                                                  </UnitLeaseContextProvider>
                                                                </LeaseContactContextProvider>
                                                              </SignatureDocumentsContextProvider>
                                                            </UnitInventoryContextProvider>
                                                          </TagContextProvider>
                                                        </CustomFieldsContextProvider>
                                                      </TechnicsContextProvider>
                                                    </TechnicsLibraryContextProvider>
                                                  </RuleContextProvider>
                                                </NotificationsContextProvider>
                                              </TicketContextProvider>
                                            </BankAccountContextProvider>
                                          </TeamsContextProvider>
                                        </UserRolesContextProvider>
                                      </UsersContextProvider>
                                    </ContactContextProvider>
                                  </EntityRelationsContextProvider>
                                </CommunicationSettingsProfileContextProvider>
                              </SettingContextProvider>
                            </UserPreferencesContextProvider>
                          </TemplateContextProvider>
                        </UnitContextProvider>
                      </BuildingSiteContextProvider>
                    </BuildingContextProvider>
                  </AddressContextProvider>
                </FilesContextProvider>
              </FolderDatabaseContextProvider>
            </PermissionsContextProvider>
          </ClientContextProvider>
        </ConfirmationDialogProvider>
      </ContextLoaderProvider>
    </React.Fragment>
  );
};

export default EntityConditionalHooks;
